// Neu (Rückmeldung 2026-09-30 „make all jobs visible with progress“) — die reinen Rechnungen des
// Aufträge-Knopfs der Kopfleiste. Keine Zahl wird hier ERFUNDEN: alles kommt aus
// GET /v/{V}/auftraege (Zählwerk, P14) oder aus dem, was der Browser selbst tut (Hochladen,
// Import, Export). Hier wird nur zusammengezählt, was schon gezählt ist.
import type { AuftraegeStand, AuftragsGruppe } from "../api/typen";
import type { ClientAuftraege } from "../store/useStore";
import { TX } from "./texte";

/** Schlüssel der Export-Erzeugung (Dialog und „Neu erzeugen“) — `useIsMutating` zählt ihn. */
export const exportSchluessel = (V: string | null | undefined) => ["export-erzeugen", V ?? ""] as const;

/** Abfragetakt: 3 s, solange etwas läuft, sonst 30 s (im verborgenen Tab gar nicht). */
export const TAKT_AKTIV_MS = 3000;
export const TAKT_RUHE_MS = 30_000;

export const offen = (g: Pick<AuftragsGruppe, "wartet" | "laeuft">) => g.wartet + g.laeuft;

/** Offene Posten, die wirklich vorangehen: GPU-Aufträge, die nach einem Stopp `wartet` bleiben,
 *  nimmt nur eine laufende Laufkette — sie zählen (wie serverseitig für `aktiv`) nur, solange der
 *  Server `aktiv` meldet. Sonst drehte der Ring und stünde die Plakette nach jedem Stopp für immer. */
export const offenLaufend = (g: AuftragsGruppe, d: Pick<AuftraegeStand, "aktiv"> | undefined) =>
  g.laeuft + (g.art === "cpu" || d?.aktiv ? g.wartet : 0);

export interface AuftragsZahlen {
  /** Offene Posten für die Plakette: Server (wartet + laeuft) plus Browser-Arbeit. */
  offen: number;
  /** Läuft irgendetwas — Server, Laufkette oder Browser? */
  aktiv: boolean;
  /** Fehlgeschlagene Aufträge der letzten 24 h. */
  fehler: number;
  /** Gesamtfortschritt der offenen Wellen (+ Lauf, wenn keine GPU-Gruppe ihn schon zählt). */
  erledigt: number;
  gesamt: number;
  /** Hochladen läuft (Browser) */
  hochladen: number;
}

export function auftragsZahlen(d: AuftraegeStand | undefined, client: ClientAuftraege, exporte: number): AuftragsZahlen {
  const gruppen = d?.gruppen ?? [];
  const server = gruppen.reduce((s, g) => s + offenLaufend(g, d), 0);
  const hoch = client.hochladen?.posten.filter((p) => p.zustand === "wartet" || p.zustand === "laedt") ?? [];
  const imp = client.import ? 1 : 0;
  const browser = hoch.length + imp + exporte;
  const summe = server + browser;

  // Fortschritt: nur Wellen mit offenen Posten (eine vor 10 min fertige Welle steht auf 100 % und
  // verfälschte die Linie). Fehler zählen als erledigt — sie laufen nicht mehr.
  let erledigt = 0;
  let gesamt = 0;
  for (const g of gruppen) {
    if (!offenLaufend(g, d)) continue;
    erledigt += g.fertig + g.fehler;
    gesamt += g.gesamt;
  }
  const l = d?.lauf;
  if (l && !gruppen.some((g) => g.schritt === l.schritt && offenLaufend(g, d) > 0)) {
    erledigt += l.seiten_fertig + l.seiten_fehler;
    gesamt += l.seiten_gesamt;
  }
  // Nur Browser-Arbeit: die Bytes der Hochladeschlange.
  if (!gesamt && hoch.length) {
    for (const p of hoch) {
      erledigt += p.geladen;
      gesamt += p.groesse;
    }
  }
  return {
    offen: summe,
    // Server-`aktiv` entscheidet für den Server (s. offenLaufend), dazu die Browser-Arbeit.
    aktiv: !!d?.aktiv || browser > 0,
    fehler: (d?.fehler ?? []).reduce((s, f) => s + f.anzahl, 0),
    erledigt: Math.min(erledigt, gesamt),
    gesamt,
    hochladen: hoch.length,
  };
}

/** Grobe Restzeit aus Sekunden je Posten × offene Posten — nie sekundengenau (wie <Fortschritt>). */
export function restzeit(sekJe: number | null | undefined, rest: number): string | null {
  if (!sekJe || sekJe <= 0 || rest <= 0) return null;
  const sek = sekJe * rest;
  if (sek < 45) return TX.auftraege.restWenig;
  if (sek < 90 * 60) return TX.auftraege.restMinuten(Math.round(sek / 60));
  return TX.auftraege.restStunden(Math.round(sek / 3600));
}

/** Die zuletzt fertig gewordene Welle (für den Leerzustand „Zuletzt fertig: …“). */
export function zuletztFertig(d: AuftraegeStand | undefined): AuftragsGruppe | null {
  let beste: AuftragsGruppe | null = null;
  for (const g of d?.gruppen ?? []) {
    if (offen(g) || !g.zuletzt_fertig || !g.fertig) continue;
    if (!beste || (beste.zuletzt_fertig ?? "") < g.zuletzt_fertig) beste = g;
  }
  return beste;
}

/** Plakettentext: höchstens zwei Stellen. */
export const plakette = (n: number) => (n > 99 ? "99+" : String(n));
